// re.rejouis.fr — VÉRIFICATIONS + acceptation obligatoire (étape bloquante).
// Trois lignes « à savoir » sans encadré, deux questions rapides (raison de la
// séparation, ancienneté — obligatoires, pour les stats de la page Datas du lab)
// et un petit mot facultatif pour l'équipe, puis des cases à cocher :
// obligatoires (certification, points du lot) puis la newsletter, facultative
// (marquée en fin de texte). Puis connexion → soumission.
import { useEffect, useState } from 'react';
import { EcranHeader, Icons, Parcours, LARGEUR_MAX } from '../components/ui.jsx';
import { fmtEuro } from '../lib/format.js';
import { repartition, libelleTotaux, ibanValide, formateIban, enregistrerVirement, lireVirement } from '../lib/rachat.js';
import { memoriserChoixNewsletter } from '../lib/newsletter.js';
import { RAISONS, ANCIENNETES, COMMENTAIRE_MAX, lireQuestionnaire, memoriserQuestionnaire } from '../lib/questionnaire.js';

// Lot dans le panier : chaque point essentiel est coché un par un (« J'ai compris
// que… ») — évite les clients qui n'ont pas lu et écrivent ensuite au SAV.
const COMPRIS_LOT = [
  <>Je n’envoie <strong>rien</strong> avant d’avoir reçu mon étiquette.</>,
  <>L’offre arrive <strong>sous 14 jours maximum</strong> : je pourrai l’accepter ou retirer le lot.</>,
  <>Sans réponse de ma part <strong>14 jours</strong> après l’offre, ma demande est <strong>annulée</strong>.</>,
  <>Mon colis contient <strong>exactement</strong> ce que j’ai validé : un jouet en plus part en don, un jouet manquant n’est pas crédité.</>,
];

function CaseCochee({ coche, onClick, children }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={coche} className="card"
      style={{ display: 'flex', gap: 12, padding: 14, alignItems: 'flex-start', textAlign: 'left', width: '100%', borderColor: coche ? 'var(--mint-fonce)' : 'var(--bord)' }}>
      <span style={{ width: 24, height: 24, borderRadius: 7, flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: coche ? 'var(--mint-fonce)' : 'var(--blanc)', border: coche ? 'none' : '1.5px solid var(--stone)' }}>
        {coche ? <Icons.check size={15} sw={2.6} style={{ color: '#fff' }} /> : null}
      </span>
      <span style={{ fontSize: 12.5, color: 'var(--noir)', lineHeight: 1.5 }}>{children}</span>
    </button>
  );
}

function Question({ titre, aide, children }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--noir)' }}>{titre}</div>
      {aide ? <div style={{ fontSize: 12, color: 'var(--stone)', marginTop: 2 }}>{aide}</div> : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>{children}</div>
    </div>
  );
}

function Choix({ actif, onClick, children }) {
  return (
    <button type="button" className={'chip' + (actif ? ' actif' : '')} aria-pressed={actif} onClick={onClick}
      style={{ whiteSpace: 'normal', textAlign: 'left', fontSize: 12.5, lineHeight: 1.35, padding: '7px 12px' }}>
      {children}
    </button>
  );
}

function Groupe({ children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
      {children}
    </div>
  );
}

export default function Verifications({ go, panier, client, onContinuer }) {
  const [accepte, setAccepte] = useState(false);
  const [compris, setCompris] = useState(() => COMPRIS_LOT.map(() => false));
  // Consentement actif : jamais pré-coché.
  const [newsletter, setNewsletter] = useState(false);
  // Réponses déjà saisies (retour arrière, aller-retour de connexion) : on les reprend.
  const [questionnaire, setQuestionnaire] = useState(() => {
    const q = lireQuestionnaire() || {};
    return { raisons: Array.isArray(q.raisons) ? q.raisons : [], anciennete: q.anciennete || '', commentaire: q.commentaire || '' };
  });
  const majQ = (champ, valeur) => setQuestionnaire((q) => ({ ...q, [champ]: valeur }));
  const basculerRaison = (r) => majQ('raisons', questionnaire.raisons.includes(r)
    ? questionnaire.raisons.filter((x) => x !== r) : [...questionnaire.raisons, r]);
  const total = panier.reduce((s, l) => s + (l.credit || 0), 0);
  // Virement choisi au panier (rachat en euros) : montant affiché à part.
  const rep = repartition(panier);
  const libelleTotal = rep.virement > 0 ? libelleTotaux(rep, fmtEuro) : fmtEuro(total);
  // Rachat en euros : coordonnées bancaires, gardées le temps de l'onglet (connexion Shopify).
  const avecVir = rep.virement > 0;
  const [vir, setVir] = useState(() => lireVirement() || { iban: '', titulaire: '' });
  const virOk = !avecVir || (ibanValide(vir.iban) && vir.titulaire.trim().length > 1);
  useEffect(() => { enregistrerVirement(avecVir ? vir : null); }, [avecVir, vir]);
  const lot = panier.some((l) => l.lot);
  const pluriel = panier.length > 1 || lot;
  const toutCompris = !lot || compris.every(Boolean);
  const questionsOk = questionnaire.raisons.length > 0 && Boolean(questionnaire.anciennete);
  const peutEnvoyer = accepte && toutCompris && questionsOk && virOk;

  // L'essentiel en trois lignes ; avec un lot, l'envoi est couvert par les cases du lot.
  const aSavoir = [
    lot ? null : 'Envoi offert via Mondial Relay (France métropolitaine), étiquette envoyée par e-mail.',
    'Tout est vérifié au labo : ton crédit est confirmé 2 jours après réception.',
    'Crédit sans date limite, valable sur toute la boutique Rejouis.',
  ].filter(Boolean);

  if (!panier.length) {
    return (
      <div style={{ padding: '60px 18px', textAlign: 'center' }}>
        <p style={{ color: 'var(--stone)', fontSize: 14 }}>Ton panier est vide.</p>
        <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => go('catalogue', { focus: true })}>Chercher un modèle</button>
      </div>
    );
  }

  return (
    <div className="no-scrollbar" style={{ height: '100%', overflow: 'auto', padding: '16px 0 150px' }}>
      <EcranHeader titre="Avant d’envoyer" onBack={() => go('panier')} />
      <Parcours etape={2} />

      <div style={{ padding: '4px 18px 0' }}>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {aSavoir.map((t, i) => (
            <li key={i} style={{ display: 'flex', gap: 8, fontSize: 12.5, color: 'var(--encre)', lineHeight: 1.5 }}>
              <Icons.check size={14} sw={2.4} style={{ color: 'var(--mint-fonce)', flexShrink: 0, marginTop: 3 }} />
              {t}
            </li>
          ))}
        </ul>

        {avecVir ? <SaisieIban vir={vir} setVir={setVir} montant={rep.virement} /> : null}

        <Question titre="Pourquoi tu souhaites t’en séparer ?" aide="Plusieurs réponses possibles.">
          {RAISONS.map((r) => (
            <Choix key={r} actif={questionnaire.raisons.includes(r)} onClick={() => basculerRaison(r)}>{r}</Choix>
          ))}
        </Question>

        <Question titre={pluriel ? 'Depuis combien de temps tu les avais ?' : 'Depuis combien de temps tu l’avais ?'}
          aide={pluriel ? 'À peu près, en moyenne.' : 'À peu près.'}>
          {ANCIENNETES.map((a) => (
            <Choix key={a} actif={questionnaire.anciennete === a} onClick={() => majQ('anciennete', questionnaire.anciennete === a ? '' : a)}>{a}</Choix>
          ))}
        </Question>

        <div style={{ marginTop: 20 }}>
          <label htmlFor="mot-equipe" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--noir)', display: 'block' }}>
            Un petit mot pour l’équipe Rejouis ? <span style={{ fontWeight: 400, color: 'var(--stone)' }}><em>Facultatif.</em></span>
          </label>
          <textarea id="mot-equipe" rows={3} maxLength={COMMENTAIRE_MAX}
            placeholder="Une précision sur tes jouets, une idée, un mot doux…"
            value={questionnaire.commentaire} onChange={(e) => majQ('commentaire', e.target.value)}
            style={{ width: '100%', marginTop: 8, padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--bord)', fontSize: 14,
              resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--blanc)', lineHeight: 1.5 }} />
        </div>

        <Groupe>
          {lot ? <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--encre)' }}>Pour mon lot :</div> : null}
          {lot ? COMPRIS_LOT.map((texte, i) => (
            <CaseCochee key={i} coche={compris[i]} onClick={() => setCompris((c) => c.map((v, j) => (j === i ? !v : v)))}>
              {texte}
            </CaseCochee>
          )) : null}
          <CaseCochee coche={accepte} onClick={() => setAccepte((a) => !a)}>
            Je certifie que {pluriel ? 'mes jouets sont' : 'mon jouet est'} <strong>propre{pluriel ? 's' : ''}</strong> et que les informations déclarées sont <strong>exactes
            et sincères</strong>. {pluriel ? 'Si un jouet n’est' : 'S’il n’est'} pas conforme, l’offre pourra être réduite ou annulée et les <strong>frais
            de retour</strong> seront à ma charge.
          </CaseCochee>
        </Groupe>

        <Groupe>
          <CaseCochee coche={newsletter} onClick={() => setNewsletter((v) => !v)}>
            Je veux recevoir les <strong>bons plans et nouveautés</strong> Rejouis par e-mail.{' '}
            <span style={{ color: 'var(--stone)' }}>Désinscription en un clic. <em>Facultatif.</em></span>
          </CaseCochee>
        </Groupe>
      </div>

      {/* Barre de validation fixe */}
      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, padding: '12px 16px 22px', zIndex: 30, maxWidth: LARGEUR_MAX, margin: '0 auto',
        background: 'linear-gradient(to top, var(--blanc) 78%, transparent)' }}>
        <button className="btn btn-primary btn-block" disabled={!peutEnvoyer} onClick={() => {
          memoriserChoixNewsletter(newsletter);
          memoriserQuestionnaire(questionnaire);
          onContinuer(accepte);
        }}>
          {client ? <>Envoyer ma demande · {libelleTotal}{lot ? ' + lot' : ''}</> : <><Icons.lock size={15} /> Me connecter et envoyer</>}
        </button>
        {!peutEnvoyer ? (
          <p style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--stone)', margin: '8px 0 0' }}>
            {!virOk ? 'Renseigne le titulaire et un IBAN valide pour ton virement.'
              : !questionsOk ? 'Réponds aux deux questions pour continuer.'
              : lot && !toutCompris ? 'Coche chaque point pour continuer.' : 'Coche la case de certification pour continuer.'}
          </p>
        ) : !client ? (
          <p style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--stone)', margin: '8px 0 0' }}>
            Connexion avec ton compte Rejouis (Google ou e-mail) pour recevoir ton crédit.
          </p>
        ) : null}
      </div>
    </div>
  );
}

// Coordonnées bancaires du virement (jouets pour lesquels le client a choisi le virement).
function SaisieIban({ vir, setVir, montant }) {
  const maj = (champ, v) => setVir((x) => ({ ...x, [champ]: v }));
  const ibanFaux = vir.iban.length >= 15 && !ibanValide(vir.iban);
  const champ = { width: '100%', padding: '11px 12px', borderRadius: 12, border: '1.5px solid var(--bord)', fontSize: 14,
    outline: 'none', boxSizing: 'border-box', background: 'var(--blanc)', color: 'var(--noir)' };
  return (
    <div className="ph-no-capture" style={{ marginTop: 20 }}>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--noir)' }}>Où verser ton virement de {fmtEuro(montant)} ?</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        <input className="ph-no-capture" style={champ} placeholder="Titulaire du compte (prénom et nom)" autoComplete="name"
          value={vir.titulaire} onChange={(e) => maj('titulaire', e.target.value.slice(0, 120))} />
        <input className="ph-no-capture" style={{ ...champ, fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: 0.5,
          borderColor: ibanFaux ? 'var(--rouge)' : 'var(--bord)' }}
          placeholder="IBAN (FR76 …)" autoComplete="off" spellCheck={false}
          value={formateIban(vir.iban)} onChange={(e) => maj('iban', e.target.value.replace(/\s/g, '').toUpperCase().slice(0, 34))} />
        {ibanFaux ? <div style={{ fontSize: 12, color: 'var(--rouge)' }}>Cet IBAN ne semble pas valide : vérifie les caractères saisis.</div> : null}
        <div style={{ fontSize: 11.5, color: 'var(--stone)', lineHeight: 1.5 }}>Ton IBAN sert uniquement à te verser ce virement.</div>
      </div>
    </div>
  );
}
